import { useEffect, useState } from "react";
import api from "../lib/api";
import { rupiah, fmtDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Eye } from "lucide-react";

const PAY_LABEL = { cash: "Tunai", transfer: "Transfer", qris: "QRIS", credit: "Hutang" };

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [filter, setFilter] = useState("");
  const [payFilter, setPayFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/sales").then((r) => setSales(r.data)); }, []);
  const filtered = sales.filter((s) => {
    const q = filter.toLowerCase();
    const match = !q || s.number.toLowerCase().includes(q) || (s.cashier || "").toLowerCase().includes(q)
      || (s.customer || "").toLowerCase().includes(q)
      || (s.items || []).some((l) => (l.name || "").toLowerCase().includes(q));
    const pm = payFilter === "all" || s.payment_method === payFilter;
    return match && pm;
  });

  return (
    <div className="space-y-5" data-testid="sales-page">
      <div>
        <h1 className="text-2xl font-bold font-display text-slate-900">Riwayat Penjualan</h1>
        <p className="text-sm text-slate-500">Seluruh transaksi kasir</p>
      </div>
      <div className="flex gap-3 flex-wrap">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cari no. nota / kasir / pelanggan / nama barang..." className="max-w-sm" data-testid="sales-search-input" />
        <Select value={payFilter} onValueChange={setPayFilter}>
          <SelectTrigger className="w-44" data-testid="sales-payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Metode</SelectItem>
            <SelectItem value="cash">Tunai</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
            <SelectItem value="qris">QRIS</SelectItem>
            <SelectItem value="credit">Hutang</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>No. Nota</TableHead><TableHead>Tanggal</TableHead><TableHead>Kasir</TableHead>
            <TableHead>Pelanggan</TableHead><TableHead>Metode</TableHead><TableHead className="text-right">Total</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-mono text-xs font-semibold">{s.number}</TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDateTime(s.created_at)}</TableCell>
                <TableCell>{s.cashier}</TableCell>
                <TableCell className="text-slate-500">{s.customer || "Umum"}</TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-xs">{PAY_LABEL[s.payment_method] || s.payment_method}</Badge></TableCell>
                <TableCell className="text-right mono font-semibold">{rupiah(s.total)}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => setDetail(s)}><Eye className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Belum ada penjualan</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-mono">{detail?.number}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">{fmtDateTime(detail.created_at)} · {detail.cashier}</div>
              <div className="space-y-1.5">
                {detail.items.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-slate-100 pb-1.5">
                    <span>{l.name} <span className="text-slate-400">x{l.qty}</span></span>
                    <span className="mono">{rupiah(l.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm pt-2">
                <div className="flex justify-between"><span>Subtotal</span><span className="mono">{rupiah(detail.subtotal)}</span></div>
                {detail.total_discount > 0 && <div className="flex justify-between text-rose-600"><span>Diskon</span><span className="mono">-{rupiah(detail.total_discount)}</span></div>}
                <div className="flex justify-between font-bold text-base border-t pt-1"><span>Total</span><span className="mono text-orange-600">{rupiah(detail.total)}</span></div>
                <div className="flex justify-between"><span>Bayar</span><span className="mono">{rupiah(detail.paid_amount)}</span></div>
                <div className="flex justify-between"><span>Kembali</span><span className="mono">{rupiah(detail.change)}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
